<template>
  <BlogPostLayout>
    <template #image>
      <BlogHeader
        :title="postData.title"
        :excerpt="postData.excerpt"
        :image="postData.image"
        :author="postData.author"
        :date="postData.date"
        :updatedAt="postData.updatedAt"
        :categories="postData.categories"
        :readTime="postData.readTime"
      />
    </template>

    <template #content>
      <article class="blog-content" lang="de">
        <section class="intro">
          <p>
            Einen <strong>virtuellen Rundgang erstellen</strong> und bei
            <strong>Google</strong> veröffentlichen: So lassen potenzielle
            Kunden Ihr Geschäft direkt in der Google-Suche und in Google Maps
            erkunden. Nutzer, die auf „Innen ansehen“ in Ihrem Google
            Business-Eintrag klicken, sehen einen interaktiven 360°-View Ihres
            Standorts – noch bevor sie Ihre Website besuchen. Dieser Leitfaden
            erklärt, wie Sie einen virtuellen Rundgang für Google erstellen,
            welche Bereiche fotografiert werden sollten und wie Sie die lokale
            SEO-Wirkung maximieren.
          </p>
          <p>
            Die kostenlose Testversion von Travvir umfasst Tour-Erstellung, Hosting mit teilbarem Link, iframe-Einbettung und E-Visits — und Pano-zu-Bild sowie Pano-zu-Video sind immer kostenlos. Laden Sie die Travvir-App herunter, um zu starten.
          </p>
        </section>

        <section>
          <h2>Virtuellen Rundgang erstellen: Google Business &amp; Maps</h2>
          <p>
            Suchanfragen wie „virtueller Rundgang erstellen Google“ meinen in
            der Praxis: 360°-Fotos aufnehmen und mit Ihrem
            <strong>Google Business-Profil</strong> verknüpfen (Street View /
            360°-Fotos). Das Ergebnis erscheint in Maps, der Suche und im
            Unternehmensprofil.
          </p>
          <ol>
            <li>Räume und Eingang mit dem Smartphone als 360°-Panoramen aufnehmen (Travvir oder Street View App).</li>
            <li>Bilder als Equirectangular-JPG exportieren bzw. in Street View veröffentlichen.</li>
            <li>Im Google Business-Profil unter Fotos → 360°-Fotos hochladen oder über Street View zuordnen.</li>
            <li>Freigabe abwarten (oft 24–72 Stunden) und die Anzeige in Maps prüfen.</li>
          </ol>
        </section>

        <section>
          <h2>Was es ist und wo es erscheint</h2>
          <p>
            360°-Fotos in Ihrem Google Business-Profil erscheinen an folgenden
            Stellen: in der <strong>Google-Suche</strong> in Ihrer
            Unternehmensbox auf der rechten Seite der Suchergebnisse, in
            <strong>Google Maps</strong>
            sowohl in der Desktop- als auch in der App-Ansicht, auf Ihrer
            <strong>Google Business-Profilseite</strong> unter dem Tab „Fotos"
            und in der <strong>Street View App</strong>, die 360°-Standorte auf
            der Karte markiert.
          </p>
          <p>
            Im Gegensatz zu statischen Fotos, die Betrachter passiv betrachten,
            können 360°-Touren <em>erkundet</em> werden – der
            Verweildauer-Effekt davon ist statistisch deutlich höher.
          </p>
        </section>

        <section>
          <h2>Drei Methoden, um 360°-Fotos hinzuzufügen</h2>

          <h3>Methode 1: Street View App</h3>
          <p>
            Laden Sie die Google Street View App herunter (iOS oder Android).
            Melden Sie sich mit Ihrem Google-Konto an. Öffnen Sie die App und
            tippen Sie auf das Plus-Symbol → „360°-Foto aufnehmen". Folgen Sie
            der Kamera-Führung, um ein vollständiges sphärisches Bild
            aufzunehmen. Tippen Sie auf „Veröffentlichen" und wählen Sie Ihren
            Google Business-Standort als Veröffentlichungsort.
          </p>

          <h3>Methode 2: Drittanbieter-App (empfohlen)</h3>
          <p>
            Verwenden Sie Travvir, um Ihre 360°-Tour zu erstellen. Die kostenlose Testversion von Travvir umfasst Tour-Erstellung, Hosting mit teilbarem Link, iframe-Einbettung und E-Visits — und Pano-zu-Bild sowie Pano-zu-Video sind immer kostenlos. Laden Sie die Travvir-App herunter, um zu starten. Unter
            <strong>Teilen → Google Street View</strong> wählen Sie Szenen und
            bestätigen Ihre Adresse. Travvir veröffentlicht über sein
            <strong>autorisiertes Street-View-Publisher-Konto</strong> — ohne
            Anmeldung mit Ihrem privaten Google-Konto in der App. Details:
            <a href="/blog/de/google-street-view">Google Street View mit Travvir</a>.
          </p>

          <h3>Methode 3: Google Trusted Photographer</h3>
          <p>
            Für Unternehmen, die einen professionellen Service bevorzugen,
            können Google Trusted Photographers beauftragt werden. Diese
            zertifizierten Fotografen erstellen und veröffentlichen
            Business-Touren direkt. Die Kosten liegen typischerweise bei 200–500
            €, abhängig von Unternehmensgröße und Standort.
          </p>
        </section>

        <section>
          <h2>Was Sie fotografieren sollten</h2>

          <h3>Restaurants und Cafés</h3>
          <p>
            Eingangsbereich und Empfang, Hauptspeiseraum aus mehreren
            Blickwinkeln, Bar- oder Theken-Bereich, Außenbereich und Terrasse
            (falls vorhanden), private Speisesäle oder besondere Bereiche.
          </p>

          <h3>Einzelhandel</h3>
          <p>
            Hauptverkaufsfläche, Auslagen und Produktpräsentation, spezielle
            Bereiche oder Segmente, Kassen- und Servicebereich, Pausen- oder
            Anproberäume.
          </p>

          <h3>Professionelle Dienstleistungen</h3>
          <p>
            Empfangsbereich und Wartebereich, Besprechungs- oder Beratungsräume,
            Büro- oder Arbeitsbereich (wenn für Kunden relevant),
            charakteristische Einrichtungsmerkmale.
          </p>

          <h3>Fitnessstudios und Gesundheitseinrichtungen</h3>
          <p>
            Haupttrainingsfläche, Cardio- und Kraftbereich, Gruppenfitnessräume,
            Umkleidekabinen und Duschen, Empfangsbereich.
          </p>
        </section>

        <section>
          <h2>Lokale SEO-Optimierung</h2>

          <h3>Quantität und Qualität</h3>
          <p>
            Google empfiehlt,
            <strong>mindestens 10 360°-Fotos</strong> hochzuladen, um den „Innen
            ansehen"-Button zu aktivieren. Mehr hochwertige Fotos führen zu
            besseren Positionen in den lokalen Suchergebnissen. Aktualisieren
            Sie Ihre Fotos saisonal oder nach einer Renovierung – frische
            Inhalte signalisieren Google, dass Ihr Profil aktiv gepflegt wird.
          </p>

          <h3>Metadaten und Tagging</h3>
          <p>
            Stellen Sie sicher, dass Ihre Fotos die korrekten GPS-Koordinaten
            haben, um dem richtigen Standort zugeordnet zu werden. Fügen Sie
            beschreibende Dateinamen hinzu, bevor Sie hochladen (z. B.
            „haupteingang-mein-restaurant-berlin.jpg"). Verknüpfen Sie Ihre
            Street View-Fotos explizit mit Ihrem Google Business-Profil.
          </p>

          <h3>Engagement-Signale</h3>
          <p>
            Stellen Sie in Ihrem Google Business-Profil stets die beste und
            neueste virtuelle Tour als Titelbild ein. Antworten Sie auf
            Bewertungen, die Ihre Räumlichkeiten erwähnen – das verstärkt die
            lokalen Suchsignale. Teilen Sie Ihre Tour-URL aktiv in sozialen
            Medien, um externe Links auf Ihr Google-Profil zu erzeugen.
          </p>
        </section>

        <section>
          <h2>Wirkung messen</h2>
          <p>
            In Ihren Google Business-Insights können Sie verfolgen: die Anzahl
            der „Innen ansehen"-Klicks, Änderungen bei den
            Wegbeschreibungsanfragen (die nach dem Hinzufügen einer Tour häufig
            steigen), Zunahme von Anruf-Klicks aus dem Profil und Steigerung der
            Seitenaufrufe.
          </p>
          <p>
            Businesses, die nach dem Hinzufügen einer 360°-Tour ihre Metriken
            beobachten, berichten durchschnittlich von einem Anstieg der
            Wegbeschreibungsanfragen um 30–50 % und einer Steigerung der
            Profilaufrufe um 20–35 %.
          </p>
        </section>

        <section>
          <h2>Fazit</h2>
          <p>
            Das Hinzufügen einer 360°-Tour zu Ihrem Google Business-Profil ist
            eine der kosteneffektivsten Marketingmaßnahmen für lokale
            Unternehmen. Sie verbessert die lokale SEO, erhöht das Engagement in
            der Google-Suche und baut Vertrauen bei potenziellen Kunden auf –
            noch bevor diese Ihre Schwelle betreten. Mit Travvir können Sie die
            Tour in weniger als einer Stunde erstellen und direkt in Google
            veröffentlichen. Die kostenlose Testversion von Travvir umfasst Tour-Erstellung, Hosting mit teilbarem Link, iframe-Einbettung und E-Visits — und Pano-zu-Bild sowie Pano-zu-Video sind immer kostenlos. Laden Sie die Travvir-App herunter, um zu starten.
          </p>
        </section>

        <section>
          <h2>Häufig gestellte Fragen</h2>
          <h3>Sind Pano-zu-Bild und Pano-zu-Video wirklich kostenlos?</h3>
          <p>
            Ja. Pano-zu-Bild und Pano-zu-Video sind in der Travvir-App immer kostenlos.
          </p>
        </section>
      
        <section>
          <h2>Weiterlesen</h2>
          <ul>
            <li><a href="/blog/de/virtual-tour-seo-leitfaden">Virtual Tour SEO : Rankings verbessern</a></li>
            <li><a href="/blog/de/360-fotos-lokales-seo">360°-Fotos für lokales SEO : Ranking verbessern</a></li>
            <li><a href="/blog/de/google-street-view-virtual-tour">Google Street View Virtual Tour : Schritt für Schritt</a></li>
            <li><a href="/blog/de/virtual-tour-kleinunternehmen">Virtual Tour Kleinunternehmen : Kunden gewinnen</a></li>
            <li><a href="/blog/de/virtual-tour-analysen">Virtual Tour Analysen : Besucher verstehen</a></li>
            <li><a href="/blog/de/virtual-tour-mit-smartphone-erstellen">Virtual Tour mit Smartphone erstellen</a></li>
          </ul>
        </section>

      </article>
    </template>
  </BlogPostLayout>
</template>

<script setup>
import blogPostsData from "~/data/blogPosts.json";

const config = useRuntimeConfig();
const cdnUrl = config.public.cdnUrl;

const postData = blogPostsData.find(
  (post) => post.slug === "google-business-virtual-tour",
);

useBlogSeo({
  title: "Virtuellen Rundgang erstellen Google [2026]: Business-Profil",
  description:
    "Virtuellen Rundgang für Google erstellen: 360°-Fotos zum Google-Business-Profil hinzufügen. Street View, Maps und lokale SEO-Anleitung.",
  keywords:
    "virtueller rundgang erstellen google, google business virtual tour, 360 fotos google business, google maps virtueller rundgang",
  slug: "google-business-virtual-tour",
  image: postData?.image,
  postData,
  lang: "de",
});

</script>
